import { motion } from "framer-motion";
import { Check, ChevronDown, Minus } from "lucide-react";
import { Checkbox as CheckboxPrimitive, Select as SelectPrimitive, Slider as SliderPrimitive, Switch as SwitchPrimitive, Tabs as TabsPrimitive } from "radix-ui";
import { forwardRef, useId, type ComponentPropsWithoutRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";

// ------------------------------------------------------------------ Switch
export function Switch({
  checked,
  onCheckedChange,
  label,
  description,
  disabled,
  size = "md",
  className,
  id,
  ...rest
}: { checked: boolean; onCheckedChange: (v: boolean) => void; label?: ReactNode; description?: ReactNode; disabled?: boolean; size?: "sm" | "md"; className?: string; id?: string; "aria-label"?: string }) {
  const auto = useId();
  const sid = id ?? auto;
  const control = (
    <SwitchPrimitive.Root
      id={sid}
      checked={checked}
      onCheckedChange={onCheckedChange}
      disabled={disabled}
      aria-label={rest["aria-label"]}
      className={cn(
        "relative inline-flex shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:shadow-[var(--ring)] disabled:opacity-45",
        size === "sm" ? "h-5 w-9" : "h-6 w-11",
        checked ? "bg-accent-solid" : "bg-line-2",
      )}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          "block rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.25)] transition-transform duration-200",
          size === "sm" ? "size-4 translate-x-0.5 data-[state=checked]:translate-x-[18px] rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-[18px]" : "size-5 translate-x-0.5 data-[state=checked]:translate-x-[22px] rtl:-translate-x-0.5 rtl:data-[state=checked]:-translate-x-[22px]",
        )}
      />
    </SwitchPrimitive.Root>
  );
  if (!label) return <span className={className}>{control}</span>;
  return (
    <div className={cn("flex items-start gap-3", className)}>
      {control}
      <label htmlFor={sid} className="min-w-0 cursor-pointer select-none">
        <span className="block text-[13.5px] font-medium text-ink">{label}</span>
        {description && <span className="mt-0.5 block text-[12px] leading-relaxed text-ink-3">{description}</span>}
      </label>
    </div>
  );
}

// ------------------------------------------------------------------ Checkbox
export const Checkbox = forwardRef<HTMLButtonElement, ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & { label?: ReactNode }>(function Checkbox({ className, label, id, ...props }, ref) {
  const auto = useId();
  const cid = id ?? auto;
  const box = (
    <CheckboxPrimitive.Root
      ref={ref}
      id={cid}
      className={cn(
        "grid size-[18px] shrink-0 place-items-center rounded-[6px] border border-line-2 bg-surface transition focus-visible:outline-none focus-visible:shadow-[var(--ring)] data-[state=checked]:border-accent data-[state=checked]:bg-accent-solid data-[state=indeterminate]:border-accent data-[state=indeterminate]:bg-accent-solid disabled:opacity-45",
        className,
      )}
      {...props}
    >
      <CheckboxPrimitive.Indicator className="text-white">{props.checked === "indeterminate" ? <Minus className="size-3" strokeWidth={3} /> : <Check className="size-3" strokeWidth={3} />}</CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
  );
  if (!label) return box;
  return (
    <div className="flex items-center gap-2">
      {box}
      <label htmlFor={cid} className="cursor-pointer select-none text-[13.5px] text-ink">
        {label}
      </label>
    </div>
  );
});

// ------------------------------------------------------------------ Select
export type SelectOption<V extends string> = { value: V; label: ReactNode; icon?: ReactNode; description?: ReactNode; disabled?: boolean };

export function Select<V extends string>({
  value,
  onValueChange,
  options,
  placeholder,
  size = "md",
  className,
  id,
  disabled,
  ...rest
}: {
  value: V | undefined;
  onValueChange: (v: V) => void;
  options: SelectOption<V>[];
  placeholder?: string;
  size?: "sm" | "md";
  className?: string;
  id?: string;
  disabled?: boolean;
  "aria-label"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}) {
  return (
    <SelectPrimitive.Root value={value} onValueChange={(v) => onValueChange(v as V)} disabled={disabled}>
      <SelectPrimitive.Trigger
        id={id}
        aria-label={rest["aria-label"]}
        aria-describedby={rest["aria-describedby"]}
        aria-invalid={rest["aria-invalid"]}
        className={cn(
          "inline-flex w-full items-center justify-between gap-2 rounded-xl border border-line bg-surface text-ink outline-none transition focus:border-accent focus:shadow-[var(--ring)] data-[placeholder]:text-ink-3 disabled:opacity-50",
          size === "sm" ? "h-8 px-2.5 text-[13px]" : "h-10 px-3.5 text-[14px]",
          className,
        )}
      >
        <span className="flex min-w-0 items-center gap-2 truncate [&_svg]:size-4">
          <SelectPrimitive.Value placeholder={placeholder} />
        </span>
        <SelectPrimitive.Icon>
          <ChevronDown className="size-4 text-ink-3" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectPrimitive.Portal>
        <SelectPrimitive.Content position="popper" sideOffset={6} className="z-[60] max-h-[min(360px,var(--radix-select-content-available-height))] min-w-[var(--radix-select-trigger-width)] overflow-hidden rounded-2xl border border-line bg-surface p-1 shadow-pop animate-fade-in">
          <SelectPrimitive.Viewport>
            {options.map((o) => (
              <SelectPrimitive.Item
                key={o.value}
                value={o.value}
                disabled={o.disabled}
                className="relative flex cursor-pointer select-none items-center gap-2 rounded-xl py-2 ps-3 pe-8 text-[13.5px] text-ink outline-none data-[disabled]:opacity-40 data-[highlighted]:bg-surface-2 [&_svg]:size-4"
              >
                {o.icon}
                <span className="min-w-0">
                  <SelectPrimitive.ItemText>{o.label}</SelectPrimitive.ItemText>
                  {o.description && <span className="block text-[11.5px] text-ink-3">{o.description}</span>}
                </span>
                <SelectPrimitive.ItemIndicator className="absolute end-2.5">
                  <Check className="size-4 text-accent" />
                </SelectPrimitive.ItemIndicator>
              </SelectPrimitive.Item>
            ))}
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

// ------------------------------------------------------------------ Slider
export function Slider({ className, ...props }: ComponentPropsWithoutRef<typeof SliderPrimitive.Root>) {
  return (
    <SliderPrimitive.Root className={cn("relative flex h-5 w-full touch-none select-none items-center", className)} {...props}>
      <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-surface-3">
        <SliderPrimitive.Range className="absolute h-full bg-accent" />
      </SliderPrimitive.Track>
      {(props.value ?? props.defaultValue ?? [0]).map((_, i) => (
        <SliderPrimitive.Thumb key={i} className="block size-5 rounded-full border-2 border-accent bg-white shadow transition focus-visible:outline-none focus-visible:shadow-[var(--ring)]" aria-label={props["aria-label"]} />
      ))}
    </SliderPrimitive.Root>
  );
}

// ------------------------------------------------------------------ Segmented (radio pills with a sliding thumb)
export function Segmented<V extends string>({
  value,
  onChange,
  options,
  size = "md",
  className,
  label,
  layoutId,
}: {
  value: V;
  onChange: (v: V) => void;
  options: { value: V; label: ReactNode; icon?: ReactNode; title?: string }[];
  size?: "sm" | "md";
  className?: string;
  label?: string;
  layoutId?: string;
}) {
  const auto = useId();
  return (
    <div role="radiogroup" aria-label={label} className={cn("inline-flex gap-1 rounded-xl bg-surface-2 p-1", className)}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            title={o.title}
            onClick={() => onChange(o.value)}
            onKeyDown={(e) => {
              const i = options.findIndex((x) => x.value === value);
              const rtl = document.dir === "rtl";
              const nextKey = rtl ? "ArrowLeft" : "ArrowRight";
              const prevKey = rtl ? "ArrowRight" : "ArrowLeft";
              if (e.key === nextKey) onChange(options[(i + 1) % options.length]!.value);
              if (e.key === prevKey) onChange(options[(i - 1 + options.length) % options.length]!.value);
            }}
            tabIndex={on ? 0 : -1}
            className={cn(
              "relative inline-flex items-center gap-1.5 rounded-lg font-medium transition-colors [&_svg]:size-3.5",
              size === "sm" ? "h-7 px-2.5 text-[12px]" : "h-8 px-3 text-[13px]",
              on ? "text-ink" : "text-ink-3 hover:text-ink-2",
            )}
          >
            {on && <motion.span layoutId={layoutId ?? `seg-${auto}`} className="absolute inset-0 rounded-lg bg-surface shadow-[0_1px_3px_rgba(0,0,0,.14)]" transition={{ type: "spring", bounce: 0.18, duration: 0.35 }} />}
            <span className="relative inline-flex items-center gap-1.5">
              {o.icon}
              {o.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------------ Tabs (Radix, SnowUI style)
export const Tabs = TabsPrimitive.Root;
export const TabsContent = TabsPrimitive.Content;

export function TabsList({ items, value, className, layoutId }: { items: { value: string; label: ReactNode; icon?: ReactNode; count?: number }[]; value: string; className?: string; layoutId?: string }) {
  const auto = useId();
  return (
    <TabsPrimitive.List className={cn("inline-flex max-w-full items-center gap-1 overflow-x-auto", className)}>
      {items.map((it) => {
        const on = it.value === value;
        return (
          <TabsPrimitive.Trigger
            key={it.value}
            value={it.value}
            className={cn("relative inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium transition-colors outline-none focus-visible:shadow-[var(--ring)] [&_svg]:size-4", on ? "text-ink" : "text-ink-3 hover:text-ink-2")}
          >
            {on && <motion.span layoutId={layoutId ?? `tabs-${auto}`} className="absolute inset-0 rounded-lg bg-surface-2" transition={{ type: "spring", bounce: 0.15, duration: 0.35 }} />}
            <span className="relative inline-flex items-center gap-1.5">
              {it.icon}
              {it.label}
              {it.count !== undefined && <span className="num rounded-md bg-surface-3 px-1.5 text-[10.5px] text-ink-3">{it.count}</span>}
            </span>
          </TabsPrimitive.Trigger>
        );
      })}
    </TabsPrimitive.List>
  );
}

// ------------------------------------------------------------------ Choice cards (radio with explanations)
export function ChoiceCards<V extends string>({
  value,
  onChange,
  options,
  cols = 3,
  label,
}: {
  value: V;
  onChange: (v: V) => void;
  options: { value: V; label: ReactNode; hint?: ReactNode; icon?: ReactNode; tone?: "accent" | "ok" | "warn" | "violet" }[];
  cols?: 1 | 2 | 3 | 4;
  label?: string;
}) {
  const grid = { 1: "grid-cols-1", 2: "grid-cols-1 sm:grid-cols-2", 3: "grid-cols-1 sm:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4" }[cols];
  return (
    <div role="radiogroup" aria-label={label} className={cn("grid gap-2", grid)}>
      {options.map((o) => {
        const on = o.value === value;
        const tone = { accent: "border-accent bg-accent-soft", ok: "border-ok bg-ok-soft", warn: "border-warn bg-warn-soft", violet: "border-violet bg-violet-soft" }[o.tone ?? "accent"];
        const fg = { accent: "text-accent-ink", ok: "text-ok", warn: "text-warn", violet: "text-violet" }[o.tone ?? "accent"];
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            onClick={() => onChange(o.value)}
            className={cn("rounded-2xl border-[1.5px] p-3 text-start transition", on ? tone : "border-transparent bg-surface-2 hover:border-line-2")}
          >
            <span className={cn("flex items-center gap-1.5 text-[13.5px] font-semibold [&_svg]:size-4", on ? fg : "text-ink")}>
              {o.icon}
              {o.label}
            </span>
            {o.hint && <span className="mt-1 block text-[12px] leading-snug text-ink-3">{o.hint}</span>}
          </button>
        );
      })}
    </div>
  );
}
